import React, { useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext } from 'providers/GlobalProvider';
import { helpContent } from 'components/BiteDB/common/HelpContent';
import HelpContentModal from 'components/BiteDB/Components/HelpContent';
import { useSnackbar } from 'notistack';
import { Redirect } from 'react-router-dom';
import PeopleAndCompany from 'components/BiteDB/PeopleAndCompany';
import DBModuleHeaderLayout from 'components/Common/Layouts/DBModuleHeaderLayout';
import BulkLinkEntitiesModal from 'components/BiteDB/Modal/BulkLinkEntitiesModal';
import { Dialog, DialogContent, DialogTitle, Modal } from '@mui/material';
import { useGetDatabasesByGroupId } from '../../api/bitedb/database/database.hooks';
import { useBulkUploadEvents } from 'hooks/useSocketIO';
import CreateDatabaseModal from 'components/BiteDB/Modal/CreateDatabase';
import RequiredLicense from 'pages/assign/RequiredLicense';
import { LicenseType } from 'pages/assign/LicenseBadge';
import LockIcon from '@mui/icons-material/Lock';

const EntityDB: React.FC = () => {
  const { cur_user } = useContext(GlobalContext);
  const { enqueueSnackbar } = useSnackbar();
  const [userHasMonitor, setUserHasMonitor] = useState(true);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const [processingDatabases, setProcessingDatabases] = useState<Set<string>>(
    new Set(),
  );
  const pendingLinkEntitiesDatabaseIdsRef = useRef<Set<string>>(new Set());
  const [linkEntitiesDatabaseId, setLinkEntitiesDatabaseId] = useState<
    string | null
  >(null);
  const [showLinkEntitiesPrompt, setShowLinkEntitiesPrompt] = useState(false);
  const [showLinkEntitiesModal, setShowLinkEntitiesModal] = useState(false);

  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [showCreateDatabaseModal, setShowCreateDatabaseModal] = useState(false);

  useBulkUploadEvents(undefined, {
    onComplete: (result) => {
      if (result.databaseId) {
        const completedDatabaseId = result.databaseId.toString();
        setProcessingDatabases((prev) => {
          const updated = new Set(prev);
          updated.delete(completedDatabaseId);
          return updated;
        });
        if (
          pendingLinkEntitiesDatabaseIdsRef.current.has(completedDatabaseId)
        ) {
          pendingLinkEntitiesDatabaseIdsRef.current.delete(completedDatabaseId);
          setLinkEntitiesDatabaseId(completedDatabaseId);
          setShowLinkEntitiesPrompt(true);
        }
        refetchDbs();
        enqueueSnackbar(
          `Entities uploaded successfully! ${
            result.duplicatesRemoved
              ? `${result.duplicatesRemoved} duplicates were removed.`
              : ''
          }`,
          { variant: 'success' },
        );
      }
    },
    onFailed: (result) => {
      console.error('[EntityDB] Bulk upload failed:', result);
      if (result.databaseId) {
        const failedDatabaseId = result.databaseId.toString();
        setProcessingDatabases((prev) => {
          const updated = new Set(prev);
          updated.delete(failedDatabaseId);
          return updated;
        });
        pendingLinkEntitiesDatabaseIdsRef.current.delete(failedDatabaseId);
        enqueueSnackbar(
          result.error || 'Entity upload failed. Please check the database.',
          { variant: 'error' },
        );
      }
    },
  });

  const handleDatabaseCreated = (databaseId: string) => {
    setProcessingDatabases((prev) => new Set(prev).add(databaseId));
  };

  const handleDatabaseUploadFailed = (databaseId: string) => {
    setProcessingDatabases((prev) => {
      const updated = new Set(prev);
      updated.delete(databaseId);
      return updated;
    });
    pendingLinkEntitiesDatabaseIdsRef.current.delete(databaseId);
  };

  const handleOfferLinkEntities = (databaseId: string) => {
    pendingLinkEntitiesDatabaseIdsRef.current.add(databaseId);
  };

  const handleConfirmLinkEntities = () => {
    setShowLinkEntitiesPrompt(false);
    setShowLinkEntitiesModal(true);
  };

  const handleDismissLinkEntities = () => {
    setShowLinkEntitiesPrompt(false);
    setLinkEntitiesDatabaseId(null);
  };

  const handleLinkEntitiesClose = () => {
    setShowLinkEntitiesModal(false);
    setLinkEntitiesDatabaseId(null);
    refetchDbs();
  };

  const requiredTiers = ['business', 'enterprise'];

  const checkSubscription = async () => {
    if (cur_user.quickcodeOnly || cur_user?.bite_super_admin === true) {
      setUserHasMonitor(true);
      return;
    }
    setUserHasMonitor(requiredTiers.includes(cur_user.license));
  };

  useEffect(() => {
    checkSubscription();
  }, [cur_user.license, cur_user.quickcodeOnly]);

  const isInteractionDisabled = !userHasMonitor;
  const normalizedLicense = (
    ['none', 'standard', 'pro', 'business', 'enterprise'].includes(
      cur_user?.license,
    )
      ? cur_user.license
      : 'none'
  ) as LicenseType;

  const groupIdForQueries = isInteractionDisabled
    ? ''
    : cur_user?.selected_team?.id;

  const {
    data: dbs,
    isLoading: dbsLoading,
    error: dbsError,
    refetch: refetchDbs,
  } = useGetDatabasesByGroupId(groupIdForQueries || '', searchTerm, {
    // The Entity Hub grid only shows entity databases; poll only while one
    // of those is screening.
    pollWhileScreeningType: 'people & companies',
  });

  const entityDatabases = Array.isArray(dbs)
    ? dbs.filter((db) => db.type === 'people & companies')
    : [];

  const total_number_of_entities = entityDatabases.reduce(
    (acc, db) => acc + (db?.nr_people_and_companies || 0),
    0,
  );

  useEffect(() => {
    if (dbsError) {
      enqueueSnackbar(`Error fetching databases: ${dbsError.message}`, {
        variant: 'error',
      });
    }
  }, [dbsError, enqueueSnackbar]);

  if (!cur_user.selected_team) {
    return <Redirect to='/' />;
  }

  const headerHelpButton = (
    <div
      className='module-header-help'
      onClick={() => setHelpModalOpen(true)}
      role='button'
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          setHelpModalOpen(true);
        }
      }}
      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
    >
      <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'>
        <g clipPath='url(#clip0_16034_866)'>
          <path
            d='M7.57484 7.50001C7.77076 6.94306 8.15746 6.47343 8.66647 6.17428C9.17547 5.87514 9.77392 5.76579 10.3558 5.8656C10.9377 5.96541 11.4655 6.26794 11.8457 6.71961C12.226 7.17128 12.4341 7.74294 12.4332 8.33334C12.4332 10 9.93317 10.8333 9.93317 10.8333M9.99984 14.1667H10.0082M18.3332 10C18.3332 14.6024 14.6022 18.3333 9.99984 18.3333C5.39746 18.3333 1.6665 14.6024 1.6665 10C1.6665 5.39763 5.39746 1.66667 9.99984 1.66667C14.6022 1.66667 18.3332 5.39763 18.3332 10Z'
            stroke='#94969C'
            strokeWidth='1.66667'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </g>
        <defs>
          <clipPath id='clip0_16034_866'>
            <rect width='20' height='20' fill='white' />
          </clipPath>
        </defs>
      </svg>
      <p style={{ margin: 0 }}>Help</p>
    </div>
  );

  const headerCreateButton = (
    <button
      className='btn-new-automate'
      disabled={isInteractionDisabled}
      style={{
        opacity: isInteractionDisabled ? 0.6 : 1,
        cursor: isInteractionDisabled ? 'not-allowed' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
      }}
      onClick={() => {
        if (isInteractionDisabled) return;
        setShowCreateDatabaseModal(true);
      }}
    >
      {isInteractionDisabled ? (
        <LockIcon fontSize='small' />
      ) : (
        <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='white' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
          <path d='M12 5v14M5 12h14' />
        </svg>
      )}
      <div>Create Database</div>
    </button>
  );

  return (
    <DBModuleHeaderLayout
      title='Entity DB'
      hideTitleRow
      handleRefresh={() => {
        if (!isInteractionDisabled) {
          refetchDbs();
        }
      }}
      onDatabaseCreated={handleDatabaseCreated}
      onDatabaseUploadFailed={handleDatabaseUploadFailed}
      onOfferLinkEntities={handleOfferLinkEntities}
    >
      <div>
        <RequiredLicense
          userLicense={normalizedLicense}
          minimumLicense='business'
        />
        <PeopleAndCompany
          databases={entityDatabases}
          nrOfEntities={total_number_of_entities}
          peopleCount={total_number_of_entities}
          isLoading={dbsLoading}
          onRefresh={() => refetchDbs()}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          processingDatabases={processingDatabases}
          onDatabaseCreated={handleDatabaseCreated}
          onDatabaseUploadFailed={handleDatabaseUploadFailed}
          onOfferLinkEntities={handleOfferLinkEntities}
          headerLeftActions={headerHelpButton}
          headerActions={headerCreateButton}
          isInteractionDisabled={isInteractionDisabled}
        />
      </div>

      <Dialog
        className='dialog_box'
        open={showLinkEntitiesPrompt}
        onClose={handleDismissLinkEntities}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: 'var(--bg-colors)',
            borderRadius: '0.5rem',
            overflowY: 'visible',
          },
        }}
      >
        <DialogTitle className='modal-header-content modal-title'>
          Define Entity Relationships
          <div
            className='audit-add-modal-head-cont-icon'
            onClick={handleDismissLinkEntities}
            style={{ cursor: 'pointer' }}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
            >
              <path
                d='M18 6L6 18M6 6L18 18'
                stroke='white'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        </DialogTitle>
        <DialogContent className='dialog-content-base'>
          <div
            className='audit-add-modal-cont-body'
            style={{ paddingTop: '12px', paddingBottom: '12px' }}
          >
            <div className='audit-add-modal-head-cont-supporting'>
              <p style={{ margin: 0 }}>
                Would you like to define relationships between entities in this
                database? You can upload a CSV file to link entities by their
                business registration number (EIN) or person ID.
              </p>
            </div>
          </div>
        </DialogContent>
        <div className='audit-modal-cont-footer'>
          <div className='audit-modal-cont-footer-content'>
            <div
              className='audit-modal-cont-footer-content-btn-cancel'
              onClick={handleDismissLinkEntities}
            >
              <div className='audit-modal-cont-footer-content-btn-text'>
                Later
              </div>
            </div>
            <div
              className='audit-modal-cont-footer-content-btn-upload'
              onClick={handleConfirmLinkEntities}
            >
              <div className='audit-modal-cont-footer-content-btn-text'>
                Yes, Link Entities
              </div>
            </div>
          </div>
        </div>
      </Dialog>

      <Modal open={showLinkEntitiesModal} onClose={handleLinkEntitiesClose}>
        <div style={{ outline: 'none' }}>
          <BulkLinkEntitiesModal
            open={showLinkEntitiesModal}
            onClose={handleLinkEntitiesClose}
            databaseId={linkEntitiesDatabaseId || ''}
            onSuccess={() => refetchDbs()}
          />
        </div>
      </Modal>

      <HelpContentModal
        isOpen={helpModalOpen}
        setIsOpen={setHelpModalOpen}
        title={helpContent?.title || 'Help'}
        description={helpContent?.description}
        helpItems={helpContent?.helpItems || []}
      />

      <Modal
        open={showCreateDatabaseModal}
        onClose={() => setShowCreateDatabaseModal(false)}
      >
        <div style={{ outline: 'none' }}>
          <CreateDatabaseModal
            open={showCreateDatabaseModal}
            handleClose={() => setShowCreateDatabaseModal(false)}
            handleRefresh={() => refetchDbs()}
            onDatabaseCreated={handleDatabaseCreated}
            onDatabaseUploadFailed={handleDatabaseUploadFailed}
            onOfferLinkEntities={handleOfferLinkEntities}
            canConfigureEntityAddon={Boolean(
              (cur_user?.selected_team?.attributes?.team_admin?.includes(
                cur_user?.basics?.email,
              ) ||
                cur_user?.role === 'owner') &&
                cur_user?.hasEntityAddon,
            )}
          />
        </div>
      </Modal>
    </DBModuleHeaderLayout>
  );
};

export default EntityDB;
